import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { APIResponse } from '../../../services/interface'
import { ApiDelete, ApiGet, ApiPost, ApiPut } from '../../../services/Service'
import { StatusEnum } from '../../type'

export type ProductCategory = 'supplement' | 'accessory'
export type ProductStatus = 'available' | 'sold_out' | 'not_available' | 'coming_soon'

export interface IShopInventoryRow {
  clinicId: string
  stock: number
  studioStatus: 'available' | 'sold_out'
}

export interface IShopProduct {
  id: string
  name: string
  category: ProductCategory
  description?: string
  suggestedUse?: string
  highlight?: string
  quantityLabel?: string
  // Price in cents.
  price: number
  imageUrl?: string
  videoUrl?: string
  status: ProductStatus
  lowStockThreshold: number
  active: boolean
  sortOrder: number
  totalStock?: number
  inventory?: IShopInventoryRow[]
}

export interface IInventoryMatrixRow {
  productId: string
  name: string
  category: ProductCategory
  lowStockThreshold: number
  studios: Record<string, { stock: number; studioStatus: string; lowStock: boolean }>
}

export interface ICreateProductDto {
  name: string
  category: ProductCategory
  description?: string
  suggestedUse?: string
  highlight?: string
  quantityLabel?: string
  price: number
  imageUrl?: string
  videoUrl?: string
  status?: ProductStatus
  lowStockThreshold?: number
  active?: boolean
  sortOrder?: number
}

export interface IStudioLite {
  id: string
  name: string
}

export interface ShopState {
  products: Array<IShopProduct>
  productsStatus: StatusEnum
  matrix: Array<IInventoryMatrixRow>
  matrixStatus: StatusEnum
  studios: Array<IStudioLite>
  postStatus: StatusEnum
  putStatus: StatusEnum
  deleteStatus: StatusEnum
  inventoryStatus: StatusEnum
  error: any
}

const initialState: ShopState = {
  products: [],
  productsStatus: StatusEnum.Idle,
  matrix: [],
  matrixStatus: StatusEnum.Idle,
  studios: [],
  postStatus: StatusEnum.Idle,
  putStatus: StatusEnum.Idle,
  deleteStatus: StatusEnum.Idle,
  inventoryStatus: StatusEnum.Idle,
  error: null,
}

// Shop endpoints return raw REST bodies (no { code, data } wrapper).
const ok = (res: APIResponse) => res && res.status >= 200 && res.status < 300

const apiErrorMessage = (res: APIResponse, fallback: string) => {
  const message = res?.data?.message
  if (Array.isArray(message)) return message.join(', ')
  if (typeof message === 'string') return message
  return fallback
}

export const getProductsAsync = createAsyncThunk('shop/getProducts', async (_, thunkAPI) => {
  const res: APIResponse = await ApiGet('/shop/admin/products')
    .then((r) => r)
    .catch((e) => e)
  if (ok(res)) return thunkAPI.fulfillWithValue(res.data)
  return thunkAPI.rejectWithValue('Failed to load products')
})

export const getInventoryMatrixAsync = createAsyncThunk('shop/getMatrix', async (_, thunkAPI) => {
  const res: APIResponse = await ApiGet('/shop/admin/inventory')
    .then((r) => r)
    .catch((e) => e)
  if (ok(res)) return thunkAPI.fulfillWithValue(res.data)
  return thunkAPI.rejectWithValue('Failed to load inventory')
})

export const postProductAsync = createAsyncThunk(
  'shop/postProduct',
  async ({ data }: { data: ICreateProductDto }, thunkAPI) => {
    const res: APIResponse = await ApiPost('/shop/admin/products', data)
      .then((r) => r)
      .catch((e) => e)
    if (ok(res)) return thunkAPI.fulfillWithValue(res.data)
    return thunkAPI.rejectWithValue(apiErrorMessage(res, 'Failed to create product'))
  },
)

export const putProductAsync = createAsyncThunk(
  'shop/putProduct',
  async ({ id, data }: { id: string; data: Partial<ICreateProductDto> }, thunkAPI) => {
    const res: APIResponse = await ApiPut(`/shop/admin/products/${id}`, data)
      .then((r) => r)
      .catch((e) => e)
    if (ok(res)) return thunkAPI.fulfillWithValue(res.data)
    return thunkAPI.rejectWithValue(apiErrorMessage(res, 'Failed to update product'))
  },
)

export const deleteProductAsync = createAsyncThunk('shop/deleteProduct', async ({ id }: { id: string }, thunkAPI) => {
  const res: APIResponse = await ApiDelete(`/shop/admin/products/${id}`)
    .then((r) => r)
    .catch((e) => e)
  if (ok(res)) return thunkAPI.fulfillWithValue({ id })
  return thunkAPI.rejectWithValue('Failed to delete product')
})

export const setStudioInventoryAsync = createAsyncThunk(
  'shop/setInventory',
  async ({ id, items }: { id: string; items: Array<{ clinicId: string; stock: number }> }, thunkAPI) => {
    const res: APIResponse = await ApiPut(`/shop/admin/products/${id}/inventory`, { items })
      .then((r) => r)
      .catch((e) => e)
    if (ok(res)) return thunkAPI.fulfillWithValue(res.data)
    return thunkAPI.rejectWithValue(apiErrorMessage(res, 'Failed to update inventory'))
  },
)

// Request a signed URL for image/video upload to GCS, then PUT the file from the client.
export const getSignedUrlAsync = createAsyncThunk(
  'shop/getSignedUrl',
  async (
    { id, fileName, contentType, kind }: { id: string; fileName: string; contentType: string; kind: 'image' | 'video' },
    thunkAPI,
  ) => {
    const res: APIResponse = await ApiPost(`/shop/admin/products/${id}/signed-url`, {
      fileName,
      contentType,
      kind,
    })
      .then((r) => r)
      .catch((e) => e)
    if (ok(res)) return thunkAPI.fulfillWithValue(res.data)
    return thunkAPI.rejectWithValue(apiErrorMessage(res, 'Failed to get upload URL'))
  },
)

// Studios list (this endpoint IS wrapped in { code, data }).
export const getStudiosAsync = createAsyncThunk('shop/getStudios', async (_, thunkAPI) => {
  const res: APIResponse = await ApiGet('/operator/clinics?page=1&limit=50')
    .then((r) => r)
    .catch((e) => e)
  if (ok(res) && res.data) {
    const list = res.data.data || res.data
    return thunkAPI.fulfillWithValue(list)
  }
  return thunkAPI.rejectWithValue('Failed to load studios')
})

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    resetPostStatus: (state) => {
      state.postStatus = StatusEnum.Idle
    },
    resetPutStatus: (state) => {
      state.putStatus = StatusEnum.Idle
    },
    patchProduct: (state, action: { payload: { id: string; changes: Partial<IShopProduct> } }) => {
      const product = state.products.find((p) => p.id === action.payload.id)
      if (product) Object.assign(product, action.payload.changes)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductsAsync.pending, (state) => {
        if (!state.products.length) state.productsStatus = StatusEnum.Pending
      })
      .addCase(getProductsAsync.fulfilled, (state, action) => {
        state.productsStatus = StatusEnum.Success
        state.products = action.payload as IShopProduct[]
      })
      .addCase(getProductsAsync.rejected, (state, action) => {
        state.productsStatus = StatusEnum.Failed
        state.error = action.payload
      })
      .addCase(getInventoryMatrixAsync.pending, (state) => {
        if (!state.matrix.length) state.matrixStatus = StatusEnum.Pending
      })
      .addCase(getInventoryMatrixAsync.fulfilled, (state, action) => {
        state.matrixStatus = StatusEnum.Success
        state.matrix = action.payload as IInventoryMatrixRow[]
      })
      .addCase(getInventoryMatrixAsync.rejected, (state, action) => {
        state.matrixStatus = StatusEnum.Failed
        state.error = action.payload
      })
      .addCase(postProductAsync.pending, (state) => {
        state.postStatus = StatusEnum.Pending
      })
      .addCase(postProductAsync.fulfilled, (state) => {
        state.postStatus = StatusEnum.Success
      })
      .addCase(postProductAsync.rejected, (state, action) => {
        state.postStatus = StatusEnum.Failed
        state.error = action.payload
      })
      .addCase(putProductAsync.pending, (state) => {
        state.putStatus = StatusEnum.Pending
      })
      .addCase(putProductAsync.fulfilled, (state) => {
        state.putStatus = StatusEnum.Success
      })
      .addCase(putProductAsync.rejected, (state, action) => {
        state.putStatus = StatusEnum.Failed
        state.error = action.payload
      })
      .addCase(deleteProductAsync.fulfilled, (state, action: any) => {
        state.deleteStatus = StatusEnum.Success
        state.products = state.products.filter((p) => p.id !== action.payload.id)
      })
      .addCase(setStudioInventoryAsync.pending, (state) => {
        state.inventoryStatus = StatusEnum.Pending
      })
      .addCase(setStudioInventoryAsync.fulfilled, (state) => {
        state.inventoryStatus = StatusEnum.Success
      })
      .addCase(setStudioInventoryAsync.rejected, (state) => {
        state.inventoryStatus = StatusEnum.Failed
      })
      .addCase(getStudiosAsync.fulfilled, (state, action) => {
        state.studios = (action.payload as any[]).map((s) => ({ id: s.id, name: s.name }))
      })
  },
})

export const { resetPostStatus, resetPutStatus, patchProduct } = shopSlice.actions
export default shopSlice.reducer
